import { Component } from '@theme/component';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * "Recently viewed products" section.
 *
 * Reads the product IDs the visitor viewed (stored in localStorage by
 * `recently-viewed-products.js` on every product page), leaves out the product
 * currently being viewed and renders the rest through the Section Rendering API
 * on the search route (the same way the predictive search shows recently viewed
 * products). When there is nothing to show, the section stays hidden.
 */
class RecentlyViewedProductsSection extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.loaded === 'true') return;

    // Load shortly before the section scrolls into view, like product recommendations do.
    this.#observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        this.#load();
      },
      { rootMargin: '0px 0px 600px 0px' }
    );
    this.#observer.observe(this.#observedElement);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  /**
   * A hidden element has no size, so observe a sentinel that is always rendered.
   * @returns {Element}
   */
  get #observedElement() {
    return this.closest('.shopify-section') || this.parentElement || this;
  }

  async #load() {
    const { sectionId, productId, maxProducts } = this.dataset;
    if (!sectionId) return;

    const max = Math.max(1, parseInt(maxProducts || '10', 10) || 10);

    // localStorage can be unavailable (e.g. blocked storage) or hold unexpected data.
    let stored = [];
    try {
      stored = RecentlyViewed.getProducts();
    } catch (error) {
      stored = [];
    }
    if (!Array.isArray(stored)) stored = [];

    const ids = stored
      .map((id) => String(id))
      .filter((id) => /^\d+$/.test(id) && id !== String(productId || ''))
      .slice(0, max);

    if (ids.length === 0) {
      this.#hideIfLive();
      return;
    }

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');

    try {
      const html = await sectionRenderer.getSectionHTML(sectionId, false, url);
      if (!html || !html.trim().length) throw new Error('Empty response');

      await morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: true });

      const hasProducts = this.querySelector('[data-has-recently-viewed="true"]');
      if (hasProducts) {
        this.hidden = false;
      } else {
        this.#hideIfLive();
      }
    } catch (error) {
      console.error('Recently viewed products error:', error);
      this.#hideIfLive();
    }
  }

  /** Keep the section visible in the theme editor so it can be selected and styled. */
  #hideIfLive() {
    if (window.Shopify?.designMode) return;
    this.hidden = true;
  }
}

if (!customElements.get('recently-viewed-products-section')) {
  customElements.define('recently-viewed-products-section', RecentlyViewedProductsSection);
}
